import { Query, useQueryClient } from '@tanstack/react-query';
import { creatorbase, creatorbaseQueries, queryHelpers } from '@round/api';

type Config = {
    purchaseOrderId?: string;
    campaignId?: number;
};

const campaignsPredicate = (query: Query, purchaseOrderId: string | undefined) => {
    const { params } = queryHelpers.parseQueryKey(
        query.queryKey as ReturnType<
            typeof creatorbaseQueries.campaigns.keys.infiniteList
        > as queryHelpers.QueryKey<creatorbase.GetCampaignsParams>
    );
    return params.exclude_allocated_to_purchase_id === purchaseOrderId?.toString();
};

const purchaseOrdersPredicate = (query: Query, campaignId: number | undefined) => {
    const { params } = queryHelpers.parseQueryKey(
        query.queryKey as ReturnType<
            typeof creatorbaseQueries.purchaseOrders.keys.list
        > as queryHelpers.QueryKey<creatorbase.GetPurchaseOrdersParams>
    );
    return params.exclude_allocated_to_campaign_id === campaignId?.toString();
};

export function useAllocationsInvalidations() {
    const queryClient = useQueryClient();

    return {
        invalidateAllocations: (config: Config = {}) => {
            // invalidate purchase orders
            queryClient.invalidateQueries({ queryKey: creatorbaseQueries.purchaseOrders.keys.lists() });

            if (config.purchaseOrderId) {
                queryClient.invalidateQueries({
                    queryKey: creatorbaseQueries.purchaseOrders.keys.detail(config.purchaseOrderId),
                });
            }

            // Reset queries using a predicate to target the purchase order select in the allocation modal.
            // This ensures we don't show the allocated purchase order in the select after an allocation is made.
            if (config.campaignId) {
                queryClient.resetQueries({
                    queryKey: creatorbaseQueries.purchaseOrders.keys.infiniteLists(),
                    predicate: (query) => purchaseOrdersPredicate(query, config.campaignId),
                });
            }

            // invalidate campaigns
            queryClient.invalidateQueries({
                queryKey: creatorbaseQueries.campaigns.keys.lists(),
            });

            queryClient.invalidateQueries({
                queryKey: creatorbaseQueries.campaigns.keys.infiniteLists(),
                type: 'inactive',
            });

            // Reset queries using a predicate to target the campaign select in the allocation modal.
            // This ensures we don't show the allocated campaign in the select after an allocation is made.
            if (config.purchaseOrderId) {
                queryClient.resetQueries({
                    queryKey: creatorbaseQueries.campaigns.keys.infiniteLists(),
                    predicate: (query) => campaignsPredicate(query, config.purchaseOrderId),
                });
            }

            if (config.campaignId) {
                queryClient.invalidateQueries({
                    queryKey: creatorbaseQueries.campaigns.keys.detail(config.campaignId),
                });
            }

            // invalidate allocations
            queryClient.invalidateQueries({
                queryKey: creatorbaseQueries.purchaseOrderLines.keys.lists(),
            });

            queryClient.invalidateQueries({
                queryKey: creatorbaseQueries.purchaseOrderLines.keys.infiniteLists(),
            });
        },
    };
}
